'use client';

/**
 * Scroll-up history pagination.
 *
 * The MOST-RECENT page of a resumed conversation arrives over the WebSocket
 * (history_restored) and seeds the cursor in the conversation store. This hook
 * loads OLDER pages on demand (scroll-up) via the REST detail endpoint, prepending
 * them to the in-memory message list. It is the consumer of the previously-unused
 * GET /api/conversations/{id}?before_seq= endpoint.
 *
 * Failure is explicit (tri-state success / has-more / error): a failed fetch keeps
 * the cursor UNCONSUMED and flips `error` so the UI can offer a retry — it never
 * silently renders as "reached the top".
 */

import { useCallback } from 'react';
import { useChatContext } from '@/contexts/ChatContext';
import { useConversationStore } from '@/stores/conversation-store';
import { conversationFetch } from '@/hooks/useConversations';
import {
  restoredMessagesToMessages,
  mergePrepend,
  type RestoredRow,
} from '@/lib/restore-messages';
import type { ConversationDetail } from '@/types/conversation';

const OLDER_PAGE_SIZE = 30;

export interface HistoryPagination {
  /** Older pages remain to load. */
  hasMore: boolean;
  /** An older-page fetch is in flight. */
  isLoading: boolean;
  /** The last fetch failed; the cursor is intact and loadOlder can retry. */
  error: boolean;
  /** Load the next-older page (no-op while loading, at the top, or — unless
   *  retrying — after an error). */
  loadOlder: () => Promise<void>;
}

export function useHistoryPagination(): HistoryPagination {
  const { setMessages } = useChatContext();
  // Subscribe to the reactive bits the UI renders; loadOlder reads the freshest
  // values (cursor/conversationId/loading) imperatively via getState() at call time.
  const hasMore = useConversationStore((s) => s.historyHasMore);
  const isLoading = useConversationStore((s) => s.historyLoading);
  const error = useConversationStore((s) => s.historyError);

  const loadOlder = useCallback(async () => {
    // Read the freshest state at call time (the scroll handler fires rapidly).
    const store = useConversationStore.getState();
    if (
      store.historyLoading ||
      !store.historyHasMore ||
      store.historyCursor == null ||
      !store.conversationId
    ) {
      return;
    }

    store.setHistoryLoading(true);
    store.setHistoryError(false);
    try {
      const detail = await conversationFetch<ConversationDetail>(
        `/${store.conversationId}?before_seq=${store.historyCursor}&limit=${OLDER_PAGE_SIZE}`
      );
      const older = restoredMessagesToMessages(
        detail.messages as unknown as RestoredRow[]
      );
      // Prepend older page, deduping at the boundary by stable DB id.
      setMessages((prev) => mergePrepend(older, prev));
      store.setHistoryPage(detail.next_before_seq ?? null, detail.has_more);
    } catch {
      // Keep the cursor UNCONSUMED so the user can retry; surface the error.
      store.setHistoryError(true);
    } finally {
      store.setHistoryLoading(false);
    }
  }, [setMessages]);

  return { hasMore, isLoading, error, loadOlder };
}
